import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, Search, ArrowRight } from "lucide-react";
import { listKnowledge, getKnowledgeArticle } from "@/lib/api";

const CATEGORIES = ["", "best_practice", "gap", "policy", "market_link", "faq", "other"];

export default function Knowledge() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState(null);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const params = {};
    // Do NOT filter by language by default — otherwise switching to Hindi/Marathi
    // hides English-language seeded articles. Admins can publish HI/MR versions
    // and users still see the full library. Language badge is shown on each card.
    if (category) params.category = category;
    if (q) params.q = q;
    listKnowledge(params).then(setItems).catch(() => setItems([]));
  }, [category, q, i18n.language]);

  const openArticle = async (slug) => {
    try { const a = await getKnowledgeArticle(slug); setSelected(a); }
    catch { /* ignore */ }
  };

  return (
    <div data-testid="page-knowledge">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-6">
        <div className="tr-eyebrow">{t("knowledge.eyebrow")}</div>
        <h1 className="mt-4 tr-section-title text-4xl lg:text-5xl flex items-center gap-3">
          <BookOpen className="w-9 h-9" /> {t("knowledge.title")}
        </h1>
        <p className="mt-4 max-w-2xl text-[var(--tr-muted)]">{t("knowledge.body")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="flex flex-wrap gap-3 items-center mt-2 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tr-muted)]" />
            <input data-testid="kd-search" className="tr-input pl-9" placeholder={t("knowledge.search_ph")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c || "all"} data-testid={`kd-cat-${c || "all"}`} onClick={() => setCategory(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em] transition-colors ${category === c ? "bg-[var(--tr-deep-green)] text-white" : "bg-white border border-[rgba(6,78,59,0.15)] text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"}`}>
                {t(`knowledge.cat_${c || "all"}`)}
              </button>
            ))}
          </div>
        </div>
        {!items ? <div className="p-8 text-[var(--tr-muted)]">{t("common.loading")}</div>
          : items.length === 0 ? <div data-testid="kd-empty" className="tr-card p-10 text-center text-[var(--tr-muted)]">{t("knowledge.empty")}</div>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="kd-grid">
              {items.map((a) => (
                <button key={a.id} data-testid={`kd-card-${a.slug}`} onClick={() => openArticle(a.slug)} className="tr-card p-6 text-left hover:-translate-y-1 transition-transform">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--tr-zest)]">{t(`knowledge.cat_${a.category}`)}</div>
                    <span className="text-[10px] font-mono uppercase text-[var(--tr-muted)]">{a.language}</span>
                  </div>
                  <h3 className="mt-2 font-display font-extrabold text-lg text-[var(--tr-deep-green)] leading-snug">{a.title}</h3>
                  {a.summary && <p className="mt-3 text-sm text-[var(--tr-muted)] leading-relaxed line-clamp-3">{a.summary}</p>}
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.14em] text-[var(--tr-deep-green)]">{t("knowledge.read_more")} <ArrowRight className="w-3 h-3" /></span>
                </button>
              ))}
            </div>
          )}
      </section>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-6" data-testid="kd-article-modal">
          <div className="tr-card max-w-3xl w-full max-h-[90vh] overflow-y-auto p-10">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--tr-zest)]">{t(`knowledge.cat_${selected.category}`)}</div>
            <h2 className="mt-2 font-display font-extrabold text-3xl text-[var(--tr-deep-green)]">{selected.title}</h2>
            {selected.summary && <p className="mt-3 text-[var(--tr-muted)] italic">{selected.summary}</p>}
            <div className="mt-6 prose prose-sm max-w-none text-[var(--tr-deep-green)] whitespace-pre-line leading-relaxed">{selected.body}</div>
            {selected.resource_url && <a href={selected.resource_url} target="_blank" rel="noreferrer" className="mt-6 inline-block underline text-[var(--tr-zest)]">External resource ↗</a>}
            <div className="mt-8 text-right">
              <button data-testid="kd-article-close" onClick={() => setSelected(null)} className="tr-pill-btn tr-btn-primary">{t("common.close")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
